import React, { useState, useEffect } from 'react';
import {
  Wrench,
  Calendar,
  AlertTriangle,
  CheckCircle,
  Clock,
  IndianRupee,
  Plus
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { maintenanceAPI, carAPI } from '../services/api';
import PageHeader from '../components/layout/PageHeader';
import Pagination from '../components/ui/Pagination';
import Card, { CardHeader, CardTitle, CardContent } from '../components/ui/Card';
import Button from '../components/ui/Button';
import Badge from '../components/ui/Badge';
import Select from '../components/ui/Select';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '../components/ui/Table';
import { formatCurrency, formatDate, formatVehicleName } from '../lib/utils';

const Maintenance = () => {
  const { user } = useAuth();
  const [maintenanceRecords, setMaintenanceRecords] = useState([]);
  const [cars, setCars] = useState([]);
  const [selectedCar, setSelectedCar] = useState('');
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('history'); // 'history' or 'upcoming'
  const [page, setPage] = useState(1);
  const PER_PAGE = 12;

  // Reset to first page when the car or tab changes.
  useEffect(() => { setPage(1); }, [selectedCar, activeTab]);

  const totalPages = Math.ceil(maintenanceRecords.length / PER_PAGE);
  const pagedRecords = maintenanceRecords.slice((page - 1) * PER_PAGE, page * PER_PAGE);

  useEffect(() => {
    fetchCars();
  }, [user]);

  useEffect(() => {
    if (selectedCar) {
      fetchMaintenanceData();
    }
  }, [selectedCar, activeTab]);

  const fetchCars = async () => {
    try {
      const response = await carAPI.getAllCars();
      const carsData = response.data.cars || response.data || [];
      setCars(carsData);
      if (carsData.length > 0) {
        setSelectedCar(carsData[0]._id);
      }
    } catch (error) {
      console.error('Error fetching cars:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchMaintenanceData = async () => {
    try {
      const response = activeTab === 'history'
        ? await maintenanceAPI.getMaintenanceHistory(selectedCar)
        : await maintenanceAPI.getUpcomingMaintenance(selectedCar);
      setMaintenanceRecords(response.data.maintenanceRecords || response.data.upcomingMaintenance || []);
    } catch (error) {
      console.error('Error fetching maintenance data:', error);
      setMaintenanceRecords([]);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      scheduled: { variant: 'warning', label: 'Scheduled', icon: Clock },
      'in-progress': { variant: 'info', label: 'In Progress', icon: Wrench },
      completed: { variant: 'success', label: 'Completed', icon: CheckCircle },
      overdue: { variant: 'danger', label: 'Overdue', icon: AlertTriangle }
    };

    const config = statusConfig[status] || statusConfig.scheduled;
    const Icon = config.icon;

    return (
      <Badge variant={config.variant}>
        <Icon className="h-3 w-3 mr-1 inline" />
        {config.label}
      </Badge>
    );
  };

  const getCarInfo = (carId) => {
    const car = cars.find(c => c._id === carId);
    return car ? formatVehicleName(car, { showYear: false, showReg: true }) : 'Unknown Car';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <p className="text-gray-600">Loading maintenance records...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Maintenance Management"
        description="Track and manage vehicle maintenance schedules"
        action={
          <Button variant="primary">
            <Plus className="h-5 w-5 mr-2" />
            Schedule Maintenance
          </Button>
        }
      />

      {cars.length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center">
            <AlertTriangle className="h-16 w-16 text-yellow-500 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No Vehicles Available</h3>
            <p className="text-gray-600 mb-4">
              Please add a vehicle first to view maintenance records.
            </p>
            <Button variant="primary" onClick={() => window.location.href = '/cars/add'}>
              <Plus className="h-5 w-5 mr-2" />
              Add Vehicle
            </Button>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Car Selector and Tabs */}
          <Card>
            <CardContent className="p-6">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div className="flex-1 max-w-md">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Select Car
                  </label>
                  <select
                    className="block w-full px-4 py-2.5 border border-gray-300 rounded-lg bg-white text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    value={selectedCar}
                    onChange={(e) => setSelectedCar(e.target.value)}
                  >
                    {cars.map(car => (
                      <option key={car._id} value={car._id}>
                        {formatVehicleName(car, { showYear: false, showReg: true })}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="flex border border-gray-200 rounded-lg overflow-hidden">
                  <button
                    className={`px-6 py-2.5 text-sm font-medium transition-colors ${
                      activeTab === 'history'
                        ? 'bg-blue-600 text-white'
                        : 'bg-white text-gray-700 hover:bg-gray-50'
                    }`}
                    onClick={() => setActiveTab('history')}
                  >
                    History
                  </button>
                  <button
                    className={`px-6 py-2.5 text-sm font-medium transition-colors border-l ${
                      activeTab === 'upcoming'
                        ? 'bg-blue-600 text-white'
                        : 'bg-white text-gray-700 hover:bg-gray-50'
                    }`}
                    onClick={() => setActiveTab('upcoming')}
                  >
                    Upcoming
                  </button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Maintenance Records Table */}
          <Card>
            <CardHeader>
              <CardTitle>
                {activeTab === 'history' ? 'Maintenance History' : 'Upcoming Maintenance'}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {maintenanceRecords.length === 0 ? (
                <div className="text-center py-12">
                  <Wrench className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    No {activeTab === 'history' ? 'maintenance history' : 'upcoming maintenance'} found
                  </h3>
                  <p className="text-gray-600 mb-4">
                    {activeTab === 'history'
                      ? 'This car has no maintenance records yet.'
                      : 'No maintenance is currently scheduled for this car.'}
                  </p>
                  <Button variant="outline">
                    <Plus className="h-5 w-5 mr-2" />
                    Schedule Maintenance
                  </Button>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Type</TableHead>
                      <TableHead>Description</TableHead>
                      <TableHead>Date</TableHead>
                      <TableHead>Mileage</TableHead>
                      <TableHead>Cost</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {pagedRecords.map((record) => (
                      <TableRow key={record._id}>
                        <TableCell>
                          <div className="flex items-center">
                            <Wrench className="h-4 w-4 text-gray-400 mr-2" />
                            <span className="font-medium">{record.type || 'General'}</span>
                          </div>
                        </TableCell>
                        <TableCell className="max-w-xs">
                          <p className="text-sm text-gray-900 truncate">
                            {record.description || 'N/A'}
                          </p>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center text-sm">
                            <Calendar className="h-4 w-4 text-gray-400 mr-2" />
                            {formatDate(record.date || record.scheduledDate)}
                          </div>
                        </TableCell>
                        <TableCell>
                          <span className="text-sm text-gray-600">
                            {record.mileage ? `${record.mileage.toLocaleString()} km` : 'N/A'}
                          </span>
                        </TableCell>
                        <TableCell>
                          {record.cost ? (
                            <div className="flex items-center text-sm font-semibold text-gray-900">
                              <IndianRupee className="h-4 w-4 text-gray-400 mr-1" />
                              {formatCurrency(record.cost)}
                            </div>
                          ) : (
                            <span className="text-sm text-gray-400">N/A</span>
                          )}
                        </TableCell>
                        <TableCell>{getStatusBadge(record.status)}</TableCell>
                        <TableCell>
                          <Button variant="ghost" size="sm">
                            View Details
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
              <Pagination page={page} totalPages={totalPages} onChange={setPage} />
            </CardContent>
          </Card>

          {/* Statistics Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600">Total Maintenance</p>
                    <h3 className="text-3xl font-bold text-gray-900 mt-2">
                      {maintenanceRecords.length}
                    </h3>
                  </div>
                  <div className="p-4 bg-blue-100 rounded-xl">
                    <Wrench className="h-8 w-8 text-blue-600" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600">Total Cost</p>
                    <h3 className="text-3xl font-bold text-gray-900 mt-2">
                      {formatCurrency(
                        maintenanceRecords.reduce((sum, record) => sum + (record.cost || 0), 0)
                      )}
                    </h3>
                  </div>
                  <div className="p-4 bg-green-100 rounded-xl">
                    <IndianRupee className="h-8 w-8 text-green-600" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600">Pending</p>
                    <h3 className="text-3xl font-bold text-gray-900 mt-2">
                      {maintenanceRecords.filter(r => r.status === 'scheduled').length}
                    </h3>
                  </div>
                  <div className="p-4 bg-yellow-100 rounded-xl">
                    <Clock className="h-8 w-8 text-yellow-600" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </>
      )}
    </div>
  );
};

export default Maintenance;
